import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { isString } from 'lodash';
import { formatMaterialNumber } from '../../../utils/DateAndNumberFunctions';
import {
  emptyValue, getCurrent, getRecommendationForMassUpdate, plantHasRecommendationsForMassUpdate
} from '../../MaterialDetails/summaryElements/SummaryComponents/Recommendations/RecommendationHelpers';
import {
  getMrpNdToPdV1SpecialRule,
  getSpecialRulePdTargetPlants as getSpecialRulePdTargetPlantsFromRules,
} from '../../../utils/mrpSpecialRules';
import { createKey } from './MassUpdateMrpHelpers';

// eslint-disable-next-line import/prefer-default-export

/*
  This function is used to get the delivery time for the supply base plant and calculate the new delivery time for the related plants.
  This is used when changing the MRP type to ND on the supply base plant, while the related plants are still on V1 or PD.
*/
export const getDeliveryTimePayload = (plant, fetchedPlant, relatedPlants, mrpsForRelatedPlants, changes, materialData) => {
  const supplier = materialData.find((d) => d?.PLANT_FACILITY_SAP_ID === plant)?.supplier;
  const deliveryTimePayload = { MrpBatch: '123', to_Mrp: [] };
  const preSchedulingChanges = [];
  if (plant === supplier) {
    const ndChanges = changes?.changelog?.filter((c) => c.changeType === 'MRPType (MassUpdate)' && c.newValue === 'ND');
    ndChanges?.forEach((change) => {
      const onshoreDeliveryTime = fetchedPlant.find(
        (mrpSetting) => mrpSetting.Plant === plant?.toString()
            && formatMaterialNumber(mrpSetting.MaterialNumber)
            === formatMaterialNumber(change.MaterialNumber),
      )?.PlannedDeliveryTimeInDays;
      if (onshoreDeliveryTime) {
        relatedPlants?.forEach((relatedPlant) => {
          const material = formatMaterialNumber(change.MaterialNumber);
          if (materialData.some((x) => x.PLANT_FACILITY_SAP_ID === relatedPlant.Plant_ID
              && x.MATERIAL_TYPE_SAP_ID === material)) {
            const changeObject = {
              MaterialNumber: material,
              Plant: relatedPlant.Plant_ID,
              PlannedDeliveryTimeInDays: String(onshoreDeliveryTime),
              PlannedDeliveryTimeInDays_X: true,
            };
            deliveryTimePayload.to_Mrp.push(changeObject);
          }
        });
      }
    });
  }

  /*
    This is used when changing the MRP type from ND to V1 or PD on a plant.
    This is used to set the SpecialProcurementType to 40 for related plants.
    Special rule for plant 1494: set SpecialProcurementType to 40 on itself.
  */
  const fromNdChanges = changes?.changelog?.filter((change) => change.changeType === 'MRPType (MassUpdate)' && (change.newValue === 'V1' || change.newValue === 'PD') && change.oldValue === 'ND');
  const allMrps = [];
  const addOrMergeChange = (changeObject) => {
    const existing = deliveryTimePayload.to_Mrp.find(
      (item) => item.MaterialNumber?.toString() === changeObject.MaterialNumber?.toString()
        && item.Plant?.toString() === changeObject.Plant?.toString(),
    );

    if (existing) {
      Object.assign(existing, changeObject);
      return;
    }

    deliveryTimePayload.to_Mrp.push(changeObject);
  };

  const getMrpForPlant = (targetPlant, material) => allMrps.find(
    (mrp) => formatMaterialNumber(mrp.MaterialNumber)?.toString() === material?.toString()
      && mrp.Plant?.toString() === targetPlant?.toString(),
  );

  const isBlank = (value) => value === undefined || value === null || value === '' || (isString(value) && value.trim() === '');

  const addOrMergePreSchedulingChange = (changeObject) => {
    const existing = preSchedulingChanges.find(
      (item) => item.MaterialNumber?.toString() === changeObject.MaterialNumber?.toString()
        && item.Plant?.toString() === changeObject.Plant?.toString(),
    );

    if (existing) {
      Object.assign(existing, changeObject);
      return;
    }

    preSchedulingChanges.push(changeObject);
  };

  if (mrpsForRelatedPlants && mrpsForRelatedPlants.length > 0) allMrps.push(...mrpsForRelatedPlants);
  if (fetchedPlant && fetchedPlant.length > 0) allMrps.push(...fetchedPlant);
  fromNdChanges.forEach((change) => {
    const triggerPlant = change.Plant?.toString() || plant?.toString();
    const triggerSupplier = materialData.find((d) => d?.PLANT_FACILITY_SAP_ID?.toString() === triggerPlant)?.supplier;
    const plantsForChange = [...(relatedPlants || [])];
    if (!plantsForChange.some((x) => x.Plant_ID?.toString() === triggerPlant)) {
      plantsForChange.push({ Plant_ID: triggerPlant });
    }
    const activeSpecialRule = getMrpNdToPdV1SpecialRule(triggerPlant);

    // Standard rule: set SpecialProcurementType to 40 for related plants.
    // For configured special plants, only explicit special pairs should be updated.
    if (!activeSpecialRule) {
      plantsForChange.forEach((relPlant) => {
        if (relPlant.Plant_ID?.toString() !== triggerSupplier?.toString()) {
          const material = formatMaterialNumber(change.MaterialNumber);
          if (materialData.some((x) => x.PLANT_FACILITY_SAP_ID === relPlant.Plant_ID
            && x.MATERIAL_TYPE_SAP_ID?.toString() === material?.toString())) {
            const mrpDataForRelatedPlant = getMrpForPlant(relPlant.Plant_ID, material);
            if (mrpDataForRelatedPlant) {
              if (!mrpDataForRelatedPlant.SpecialProcurementType || mrpDataForRelatedPlant.SpecialProcurementType === '') {
                const changeObject = {
                  MaterialNumber: material,
                  Plant: relPlant.Plant_ID,
                  SpecialProcurementType_X: true,
                  SpecialProcurementType: '40',
                };
                addOrMergeChange(changeObject);
              }
            }
          }
        }
      });
    }

    const material = formatMaterialNumber(change.MaterialNumber);

    if (activeSpecialRule) {
      (activeSpecialRule.preSchedulingMarginTargetsIfBlank || []).forEach((targetPlant) => {
        const mrpDataForPlant = getMrpForPlant(targetPlant, material);
        // Skip if this plant is also in specialProcurementPlants; scheduling margin will ride on that change.
        if (mrpDataForPlant && isBlank(mrpDataForPlant.SchedulingMarginKeyFloats)
          && !(activeSpecialRule.specialProcurementPlants || []).includes(targetPlant)) {
          addOrMergePreSchedulingChange({
            MaterialNumber: material,
            Plant: targetPlant,
            SchedulingMarginKeyFloats_X: true,
            SchedulingMarginKeyFloats: '001',
          });
        }
      });

      activeSpecialRule.specialProcurementPlants.forEach((targetPlant) => {
        const mrpDataForPlant = getMrpForPlant(targetPlant, material);
        if (mrpDataForPlant
          && (!mrpDataForPlant.SpecialProcurementType || mrpDataForPlant.SpecialProcurementType === '')) {
          const changeObject = {
            MaterialNumber: material,
            Plant: targetPlant,
            SpecialProcurementType_X: true,
            SpecialProcurementType: '40',
          };

          if ((activeSpecialRule.preSchedulingMarginTargetsIfBlank || []).includes(targetPlant)
            && isBlank(mrpDataForPlant.SchedulingMarginKeyFloats)) {
            changeObject.SchedulingMarginKeyFloats_X = true;
            changeObject.SchedulingMarginKeyFloats = '001';
          }

          addOrMergeChange(changeObject);
        }
      });

      (activeSpecialRule.plannedDeliveryTargets || []).forEach((targetRule) => {
        const mrpDataForPlant = getMrpForPlant(targetRule.plant, material);
        if (mrpDataForPlant
          && mrpDataForPlant.PlannedDeliveryTimeInDays?.toString()
            !== targetRule.plannedDeliveryTimeInDays?.toString()) {
          addOrMergeChange({
            MaterialNumber: material,
            Plant: targetRule.plant,
            PlannedDeliveryTimeInDays_X: true,
            PlannedDeliveryTimeInDays: `${targetRule.plannedDeliveryTimeInDays}`,
          });
        }
      });

      if (triggerPlant === '1494') {
        const mrpDataForTriggerPlant = getMrpForPlant(triggerPlant, material);
        if (mrpDataForTriggerPlant && isBlank(mrpDataForTriggerPlant.SchedulingMarginKeyFloats)) {
          addOrMergeChange({
            MaterialNumber: material,
            Plant: triggerPlant,
            SchedulingMarginKeyFloats_X: true,
            SchedulingMarginKeyFloats: '001',
          });
        }
      }

      activeSpecialRule.pdTargets.forEach((targetRule) => {
        const targetPlant = targetRule.plant;
        const mrpDataForPlant = getMrpForPlant(targetPlant, material);
        if (mrpDataForPlant) {
          if (targetRule.onlyIfMrpType && mrpDataForPlant.MRPType !== targetRule.onlyIfMrpType) {
            return;
          }

          const shouldSetMrpType = mrpDataForPlant.MRPType !== 'PD';
          const shouldSetLotSize = mrpDataForPlant.LotSize !== 'EX';
          const shouldSetController = targetRule.setMrpControllerIfBlank && isBlank(mrpDataForPlant.MRPController);
          const shouldSetSchedulingMargin = targetRule.setSchedulingMarginIfBlank
            && isBlank(mrpDataForPlant.SchedulingMarginKeyFloats);

          if (shouldSetMrpType || shouldSetLotSize || shouldSetController || shouldSetSchedulingMargin) {
            const changeObject = {
              MaterialNumber: material,
              Plant: targetPlant,
            };

            if (shouldSetMrpType) {
              changeObject.MRPType_X = true;
              changeObject.MRPType = 'PD';
            }

            if (shouldSetLotSize) {
              changeObject.LotSize_X = true;
              changeObject.LotSize = 'EX';
            }

            if (shouldSetController) {
              changeObject.MRPController_X = true;
              changeObject.MRPController = targetRule.mrpControllerValue || '000';
            }

            if (shouldSetSchedulingMargin) {
              changeObject.SchedulingMarginKeyFloats_X = true;
              changeObject.SchedulingMarginKeyFloats = '001';
            }

            addOrMergeChange(changeObject);
          }
        }
      });
    }
  });

  if (preSchedulingChanges.length > 0) {
    deliveryTimePayload.to_Mrp = [...preSchedulingChanges, ...deliveryTimePayload.to_Mrp];
  }

  return deliveryTimePayload;
};

/*
  Returns the list of pdTarget plant IDs for a given trigger plant.
  Used by MassUpdateMrp to pre-fetch MRP data for special-rule targets that may not
  appear in the standard DB plant relationships.
*/
export const getSpecialRulePdTargetPlants = (plant) => getSpecialRulePdTargetPlantsFromRules(plant);

/*
  This function is used to get the initial values for the mass update, include recommended and user defined values.
*/
export const getInitialValuesForMassUpdate = (materialData, newRecTableData, mrpData, plant) => {
  const temp = {};
  mrpData.forEach((mrp) => {
    const material = formatMaterialNumber(mrp.MaterialNumber);
    const correctMaterialData = materialData.find((m) => m?.MATERIAL_TYPE_SAP_ID?.toString() === material?.toString() && m?.PLANT_FACILITY_SAP_ID?.toString() === plant?.toString());
    const hasRecs = plantHasRecommendationsForMassUpdate(newRecTableData, correctMaterialData, mrp);
    const key = createKey(mrp.Plant, material);
    temp[key] = {};
    newRecTableData.forEach((mrpSetting) => {
      if (mrpSetting.key in mrp) {
        const current = getCurrent(mrp, mrpSetting, true);
        const recommended = getRecommendationForMassUpdate(mrpSetting.key, correctMaterialData, mrp, true);
        temp[key].current = { ...temp[key].current, [mrpSetting.key]: current };
        temp[key].recommended = { ...temp[key].recommended, [mrpSetting.key]: recommended };
        if (!hasRecs) {
          temp[key].userDefined = { ...temp[key].userDefined, [mrpSetting.key]: current };
        } else if (!recommended && (current === '0' || current === 0 || current === emptyValue)) {
          temp[key].userDefined = { ...temp[key].userDefined, [mrpSetting.key]: 0 };
        } else {
          temp[key].userDefined = { ...temp[key].userDefined, [mrpSetting.key]: recommended };
        }
        if (temp[key].userDefined[mrpSetting.key] === '-') temp[key].userDefined[mrpSetting.key] = '';
      }
    });
  });
  return temp;
};

const downloadFile = (wb, fileName) => {
  wb.xlsx.writeBuffer(fileName).then((x) => {
    const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const blob = new Blob([x], { type: fileType });
    saveAs(blob, fileName);
  }).catch((err) => {
    console.log(err.message);
  });
};

export const downloadNewStockResults = async (formRows, excludeList, dropdownValues) => {
  const selectedRows = formRows.map((row) => ({ ...row, selected: excludeList.includes(row.MATERIAL_TYPE_SAP_ID) ? 'Unselected' : 'Selected', MATERIAL_TYPE_SAP_ID: Number(row.MATERIAL_TYPE_SAP_ID) }));
  const wb = new ExcelJS.Workbook();
  const worksheet = wb.addWorksheet('My Sheet');
  const idCol = worksheet.getColumn('A');
  idCol.width = 50;
  idCol.type = ExcelJS.ValueType.Boolean;
  idCol.style = {
    alignment: {
      horizontal: 'center',
      vertical: 'middle',
    },
  };

  const mrpControllerCopy = JSON.parse(JSON.stringify(dropdownValues.MRPController));
  const mrpGroupCopy = JSON.parse(JSON.stringify(dropdownValues.MRPGroup));
  const plantMaterialCopy = JSON.parse(JSON.stringify(dropdownValues.PlantMaterialStatus));

  const MRPController = mrpControllerCopy.map((val) => `'${val}`);
  const MRPGroup = mrpGroupCopy.map((val) => `'${val}`);
  const PlantMaterialStatus = plantMaterialCopy.map((val) => `'${val}`);

  const protectedColumns = [1, 2, 3, 4, 5, 6, 7];

  const validationObj = {
    type: 'list',
    allowBlank: true,
    showErrorMessage: true,
    errorStyle: 'error',
    errorTitle: 'Invalid',
    error: 'Only values provided in the dropdown are considered valid',
  };

  worksheet.columns = [
    {
      header: 'Selected',
      key: 'selected',
      width: 10,
      style: { numFmt: '@' },
    },
    {
      header: 'Plant', key: 'PLANT_FACILITY_SAP_ID', width: 8,
    },
    {
      header: 'Material', key: 'MATERIAL_TYPE_SAP_ID', width: 12,
    },
    {
      header: 'Material description', key: 'EDAM_MATERIAL_TYPE_NM', width: 25,
    },
    {
      header: 'Max Crit', key: 'CC_MAX_CRITICALITY', width: 12,
    },
    {
      header: 'Fair Price', key: 'FAIR_PRICE', width: 12,
    },
    {
      header: 'Total Stock', key: 'TOT_STOCK', width: 8,
    },
    {
      header: 'Open Docs', key: 'docCount', width: 8,
    },
    {
      header: 'MRP Type', key: 'MRPType', width: 8, style: { numFmt: '@' },
    },
    {
      header: 'Lot Size', key: 'LotSize', width: 6,
    },
    {
      header: 'Reorder Point', key: 'ReorderPoint', width: 10,
    },
    {
      header: 'Minimum Lot Size', key: 'MinimumLotSize', width: 12,
    },
    {
      header: 'Maximum Stock Level', key: 'MaximumStockLevel', width: 12,
    },
    {
      header: 'Rounding value', key: 'RoundingValue', width: 12,
    },
    {
      header: 'Fixed Lot Size', key: 'FixedLotSize', width: 12,
    },
    {
      header: 'MRP Group', key: 'MRPGroup', width: 12, style: { numFmt: '@' },
    },
    {
      header: 'MRP Controller', key: 'MRPController', width: 12, style: { numFmt: '@' },
    },
    {
      header: 'Plant Material Status', key: 'PlantMaterialStatus', width: 12, style: { numFmt: '@' },
    },
    {
      header: 'ABC Indicator', key: 'ABCIndicator', width: 12, style: { numFmt: '@' },
    },
    {
      header: 'Monetary Impact', key: 'monetaryImpact', width: 12,
    },
    {
      header: 'Monetary Impact Type', key: 'monetaryImpactType', width: 12,
    },
  ];

  const sortFunction = (a, b) => {
    if (a.MATERIAL_TYPE_SAP_ID === b.MATERIAL_TYPE_SAP_ID) {
      return 0;
    }

    return (a.MATERIAL_TYPE_SAP_ID < b.MATERIAL_TYPE_SAP_ID) ? -1 : 1;
  };

  const sorted = selectedRows.sort(sortFunction);

  for (let i = 0; i < selectedRows.length; i++) {
    worksheet.addRow({ ...sorted[i] });
  }

  const MRPTypeDropdown = dropdownValues?.MRPType?.join();
  const ABCDropdown = dropdownValues?.ABCIndicator?.join();
  const MRPGroupDropdown = MRPGroup?.join();
  const StatusDropdown = PlantMaterialStatus?.join();
  const LotSizeDropdown = dropdownValues?.LotSize?.join();
  const MRPControllerDropdown = MRPController?.join();

  for (let i = 1; i < worksheet.actualRowCount; i++) {
    worksheet.getCell('A'.concat(i + 1)).dataValidation = {
      formulae: ['"Selected,Unselected"'],
      ...validationObj,
    };
    worksheet.getCell('U'.concat(i + 1)).dataValidation = {
      formulae: ['"No impact,Savings,Investment"'],
      alignment: {
        horizontal: 'center',
      },
      ...validationObj,
    };
    worksheet.getCell('I'.concat(i + 1)).dataValidation = {
      formulae: [`"${MRPTypeDropdown}"`],
      alignment: {
        horizontal: 'center',
      },
      ...validationObj,
    };
    worksheet.getCell('J'.concat(i + 1)).dataValidation = {
      formulae: [`"${LotSizeDropdown}"`],
      ...validationObj,
    };
    worksheet.getCell('P'.concat(i + 1)).dataValidation = {
      formulae: [`"${MRPGroupDropdown}"`],
      ...validationObj,
    };
    worksheet.getCell('Q'.concat(i + 1)).dataValidation = {
      formulae: [`"${MRPControllerDropdown}"`],
      ...validationObj,
    };
    worksheet.getCell('R'.concat(i + 1)).dataValidation = {
      formulae: [`"${StatusDropdown}"`],
      ...validationObj,
    };
    worksheet.getCell('S'.concat(i + 1)).dataValidation = {
      formulae: [`"${ABCDropdown}"`],
      ...validationObj,
    };
  }

  for (let i = 0; i < 21; i++) {
    const x = worksheet.getCell(String.fromCharCode('A'.charCodeAt(0) + i).concat(1));
    x.font = {
      bold: true,
      size: 12,
    };
    x.alignment = {
      wrapText: true,
    };
  }

  worksheet.eachRow((row, i) => {
    row.protection = { locked: i === 1 };
    row.eachCell((cell) => {
      cell.protection = {
        locked: i === 1,
        lockText: i === 1,
      };
    });
  });

  worksheet.columns.forEach((column, columnIndex) => {
    if (protectedColumns.includes(columnIndex)) {
      column.eachCell((cell) => {
        cell.protection = {
          lockText: true,
          locked: true,
        };
      });
    }
  });

  await worksheet.protect('', { selectLockedCells: true, selectUnlockedCells: true, deleteRows: true });

  downloadFile(wb, 'MRP_data.xlsx');
};

export const readXLSX = (data, updateValuesFromImport) => {
  const wb = new ExcelJS.Workbook();
  const uploadRows = [];
  wb.xlsx.load(data).then((workbook) => {
    workbook.eachSheet((sheet) => {
      sheet.eachRow((row, rowIndex) => {
        if (rowIndex < 2) return;
        const vals = [...row.values];
        uploadRows.push({
          selected: vals[1],
          PLANT_FACILITY_SAP_ID: vals[2],
          MATERIAL_TYPE_SAP_ID: vals[3],
          EDAM_MATERIAL_TYPE_NM: vals[4],
          CC_MAX_CRITICALITY: vals[5],
          FAIR_PRICE: vals[6],
          TOT_STOCK: vals[7],
          docCount: vals[8],
          MRPType: vals[9],
          LotSize: vals[10],
          ReorderPoint: vals[11],
          MinimumLotSize: vals[12],
          MaximumStockLevel: vals[13],
          RoundingValue: vals[14],
          FixedLotSize: vals[15],
          MRPGroup: vals[16],
          MRPController: vals[17],
          PlantMaterialStatus: vals[18],
          ABCIndicator: vals[19],
          monetaryImpact: vals[20],
          monetaryImpactType: vals[21],
        });
      });
    });
    updateValuesFromImport(uploadRows);
  }).catch((error) => console.log(error));
};

const checkIfHasMonImps = (key, monImps) => {
  const impact = monImps?.find((impact) => createKey(impact.plant, impact.material) === key);

  if (impact) {
    return {
      monetaryImpact: impact.impact,
      monetaryImpactType: impact.monetaryImpactType,
    };
  }
  return {
    monetaryImpact: '0',
    monetaryImpactType: 'No impact',
  };
};

export const generateExcelValues = (input, original, monImps) => {
  Object.entries(original).map((og) => {
    if (input[og[0]]) {
      Object.entries(input[og[0]]).forEach((inp) => {
        const key = inp[0];
        // eslint-disable-next-line prefer-destructuring
        if (key) og[1][key] = inp[1];
      });
    } else {
      Object.entries(og[1]).forEach((entry) => {
        // eslint-disable-next-line prefer-destructuring
        if (isString(entry[1]) && entry[1].includes('.000')) {
          const noDecimal = entry[1].split('.')[0];
          og[1][entry[0]] = noDecimal;
        }
      });
    }
  });

  Object.entries(original).map((og) => {
    const key = og[0];
    // eslint-disable-next-line prefer-destructuring
    const impactData = checkIfHasMonImps(key, monImps);
    og[1].monetaryImpact = impactData.monetaryImpact;
    og[1].monetaryImpactType = impactData.monetaryImpactType;
  });
  const asRows = Object.values(original);
  return asRows;
};

const isMaxCrit5Valid = (maxCrit, leadTime) => {
  if (maxCrit === '5' && leadTime < 60) {
    return true;
  }
  return false;
};

const isMaxCrit9Valid = (maxCrit, leadTime) => {
  if (maxCrit === '9' && leadTime < 15) {
    return true;
  }
  return false;
};

export const checkIfMaterialIsAccordingToStrategy = (material, orgMaterial) => {
  if (material.CC_CONSUMPTION_SEGMENT_GROUP === 'FM' || orgMaterial.ABCIndicator === 'A') {
    return false;
  }
  if (material.PLANT_TYPE === 'INSTALLATION' && material.CC_MAX_CRITICALITY !== '3') {
    return true;
  } if (material.PLANT_TYPE === 'SUPPLY BASE') {
    const validMaxCrit = ['2', '3', '5', '6', '9'];
    if (!validMaxCrit.includes(material.CC_MAX_CRITICALITY) || isMaxCrit5Valid(material.CC_MAX_CRITICALITY, material.CC_LEAD_TIME) || isMaxCrit9Valid(material.CC_MAX_CRITICALITY, material.CC_LEAD_TIME)) {
      return true;
    }
  }
  return false;
};

export const lockedColumns = ['CC_MAX_CRITICALITY', 'PLANT_FACILITY_SAP_ID', 'MATERIAL_TYPE_SAP_ID', 'TOT_STOCK', 'docCount', 'monetaryImpact', 'monetaryImpactType', 'FAIR_PRICE', 'EDAM_MATERIAL_TYPE_NM'];
